import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface HealthMetricsGuideModalProps {
  open: boolean;
  onClose: () => void;
  metricType: 'sleep' | 'hydration' | 'nutrition';
}

const HealthMetricsGuideModal: React.FC<HealthMetricsGuideModalProps> = ({
  open,
  onClose,
  metricType
}) => {
  const getMetricData = () => {
    switch (metricType) {
      case 'sleep':
        return {
          title: 'Sleep Score',
          icon: '🛏️',
          data: [
            { score: 1, label: 'Extremely Poor', description: 'Barely slept, if at all.', color: '#ffebee' },
            { score: 2, label: 'Very Poor', description: 'Short and disrupted. Body feels exhausted.', color: '#ffcdd2' },
            { score: 3, label: 'Poor', description: 'Got some sleep, but restless. Feeling groggy.', color: '#ef9a9a' },
            { score: 4, label: 'Below Average', description: 'Slept longer, but still restless. Noticeably fatigued.', color: '#ffcc80' },
            { score: 5, label: 'Fair', description: 'Mediocre sleep. Not fully rested.', color: '#ffb74d' },
            { score: 6, label: 'Decent', description: 'Slept reasonably well. Feeling okay, but not great.', color: '#fff176' },
            { score: 7, label: 'Good', description: 'Solid night of sleep with minor interruptions. Feeling alert.', color: '#dce775' },
            { score: 8, label: 'Very Good', description: 'Slept deeply. Feeling refreshed.', color: '#aed581' },
            { score: 9, label: 'Excellent', description: '8+ hours of steady sleep. Woke up energized and ready.', color: '#81c784' },
            { score: 10, label: 'Perfect', description: 'Multiple (5+) days of Excellent Sleep in a row.', color: '#66bb6a' },
          ]
        };
      case 'hydration':
        return {
          title: 'Hydration Score',
          icon: '💧',
          data: [
            { score: 1, label: 'Extremely Poor', description: 'Brown (cola) urine, possible headache.', color: '#ffebee' },
            { score: 2, label: 'Very Poor', description: 'Light brown urine, thirsty and sluggish.', color: '#ffcdd2' },
            { score: 3, label: 'Poor', description: 'Dark orange urine, low energy.', color: '#ef9a9a' },
            { score: 4, label: 'Below Average', description: 'Light orange urine, slight headache/feeling heavy.', color: '#ffcc80' },
            { score: 5, label: 'Fair', description: 'Dark yellow urine, thirst comes and goes.', color: '#ffb74d' },
            { score: 6, label: 'Decent', description: 'Yellow urine, feeling okay but not great.', color: '#fff176' },
            { score: 7, label: 'Good', description: 'Light yellow urine, solid hydration.', color: '#dce775' },
            { score: 8, label: 'Very Good', description: 'Pale yellow (lemonade) urine, well hydrated.', color: '#aed581' },
            { score: 9, label: 'Excellent', description: 'Very light yellow, nearly clear. Ideal balance.', color: '#81c784' },
            { score: 10, label: 'Perfect', description: 'Multiple (5+) days of Excellent Hydration in a row.', color: '#66bb6a' },
          ]
        };
      case 'nutrition':
        return {
          title: 'Nutrition Score',
          icon: '🍽️',
          data: [
            { score: 1, label: 'Extremely Poor', description: 'Skipped meals or barely eaten. Mostly junk food. No fuel.', color: '#ffebee' },
            { score: 2, label: 'Very Poor', description: '1 meal + snacks. Missing key nutrients. Feeling drained.', color: '#ffcdd2' },
            { score: 3, label: 'Poor', description: 'Caloric deficit. Maybe two meals. Unbalanced.', color: '#ef9a9a' },
            { score: 4, label: 'Below Average', description: 'Poor caloric choices (high sugar, low protein). Inconsistent energy.', color: '#ffcc80' },
            { score: 5, label: 'Fair', description: '2-3 full meals. Mostly healthy with some missing nutrients.', color: '#ffb74d' },
            { score: 6, label: 'Decent', description: 'Met basic caloric needs. Decent carbs with some protein.', color: '#fff176' },
            { score: 7, label: 'Good', description: '3 balanced meals with snacks. Decent carbs with decent protein.', color: '#dce775' },
            { score: 8, label: 'Very Good', description: 'Balanced day. Adequate carbs + protein.', color: '#aed581' },
            { score: 9, label: 'Excellent', description: 'Optimal fueling. Lean protein, whole grains, and vegetables.', color: '#81c784' },
            { score: 10, label: 'Perfect', description: 'Multiple (5+) days of Excellent Nutrition.', color: '#66bb6a' },
          ]
        };
      default:
        return { title: '', icon: '', data: [] };
    }
  };

  const metricData = getMetricData();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="h5" component="span" sx={{ mr: 1 }}>
              {metricData.icon}
            </Typography>
            <Typography variant="h5" component="span">
              {metricData.title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '15%' }}>
                  Score
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '25%' }}>
                  Score
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', width: '60%' }}>
                  Score Guide
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metricData.data.map((row) => (
                <TableRow
                  key={row.score}
                  sx={{
                    backgroundColor: row.color,
                    '&:hover': {
                      backgroundColor: row.color,
                      filter: 'brightness(0.95)',
                    },
                  }}
                >
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', border: '1px solid #e0e0e0' }}>
                    {row.score}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', border: '1px solid #e0e0e0' }}>
                    {row.label}
                  </TableCell>
                  <TableCell sx={{ border: '1px solid #e0e0e0' }}>
                    {row.description}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{ minWidth: 120 }}
        >
          Got it!
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HealthMetricsGuideModal;
